import { PurchaseHistoryRow, LineItem, FulfillmentType } from '../../components/common/types';
import { RawActiveDistribution } from '../types';
import { orUndefined } from '../../utils';

const FULFILLMENT_TYPES: FulfillmentType[] = ['DELIVERY', 'PICKUP', 'LOCKER'];

function toFulfillmentType(distributionType: string | null | undefined): FulfillmentType {
  return (FULFILLMENT_TYPES as string[]).includes(distributionType ?? '')
    ? (distributionType as FulfillmentType)
    : 'DELIVERY';
}

function toLineItems(items: RawActiveDistribution['lineItems']): LineItem[] {
  return (items ?? []).map((item) => ({
    name: item.name ?? 'Item',
    imageUrl: orUndefined(item.imageUrl)
  }));
}

// lineItems carry their own totalPrice inline (unlike purchaseHistory/orderDetails, which have no
// order-level total field at all for activeOrders) — sum what's present rather than leaving every
// row's totalAmount unset when the data is actually available.
function sumLineItemTotals(items: RawActiveDistribution['lineItems']): number | undefined {
  const totals = (items ?? []).map((item) => item.totalPrice).filter((value): value is number => value != null);
  return totals.length > 0 ? totals.reduce((sum, value) => sum + value, 0) : undefined;
}

/**
 * A stable row identity for a distribution. `orderNumber` alone is not guaranteed unique — the
 * confirmed sample response shows split-fulfillment orders (the same order producing a delivery
 * and a pickup distribution) sharing an `orderNumber` with different `distributionType`s, which
 * is exactly what ActiveOrdersTable's existing (pre-connection) `rowKey` composite already
 * anticipated. Formalizes that composite into one documented, reusable helper.
 */
export function getActiveOrderKey(distribution: RawActiveDistribution): string {
  return [distribution.orderNumber, distribution.distributionType].filter(Boolean).join('-');
}

/**
 * "View Order Details" navigation target — a real anchor href, replicating AllOrdersTable's own
 * decision (see ADR-002/ADR-003) rather than a host-supplied callback. Mirrors
 * `buildOrderDetailsPageUrl`'s shape (same base route, params appended only when present) but
 * with activeOrders' own identifying fields — there is no salesDate/storeNumber/transactionId/
 * registerNumber/transactionType/orderOrigin here the way purchaseHistory has; `orderNumber`,
 * `distributionType`, and `workOrderNumber` are what's available instead. Unconfirmed against a
 * real host route, same caveat as `buildOrderDetailsPageUrl`.
 */
export function buildActiveOrderDetailsPageUrl(distribution: RawActiveDistribution): string {
  const params = new URLSearchParams();
  const append = (key: string, value: string | null | undefined) => {
    if (value != null && value !== '') {
      params.set(key, value);
    }
  };

  append('orderNumber', distribution.orderNumber);
  append('distributionType', distribution.distributionType);
  append('workOrderNumber', distribution.workOrderNumber);
  append('POJobName', distribution.poJobName);

  const query = params.toString();
  return query ? `/myaccount/order-details?${query}` : '/myaccount/order-details';
}

/**
 * "Track Delivery" navigation target — only built for DELIVERY distributions carrying a
 * `trackingNumber`; PICKUP distributions and DELIVERY distributions without one get no
 * `trackDeliveryUrl` at all (see `PurchaseHistoryRow.trackDeliveryUrl`'s own doc comment), rather
 * than a URL that would land on a page with nothing to track. Same base-route convention as
 * `buildActiveOrderDetailsPageUrl` — unconfirmed against a real host route.
 */
export function buildTrackDeliveryUrl(distribution: RawActiveDistribution): string | undefined {
  if (distribution.distributionType !== 'DELIVERY' || !distribution.trackingNumber) {
    return undefined;
  }

  const params = new URLSearchParams();
  params.set('orderNumber', distribution.orderNumber ?? '');
  params.set('trackingNumber', distribution.trackingNumber);
  return `/myaccount/track-delivery?${params.toString()}`;
}

/**
 * Maps a raw activeOrders distribution into the PurchaseHistoryRow shape ActiveOrdersTable
 * renders. Unlike AllOrdersTable's purchaseHistory/orderDetails split, activeOrders returns
 * everything a row needs inline — no second per-row query or enrichment step is involved.
 *
 * `workOrderNumber`/`instructions` are intentionally not surfaced as their own row fields — no
 * PurchaseHistoryRow field exists for them and no acceptance criteria requires displaying them
 * directly (see ADR-003); `workOrderNumber` is still consumed internally by
 * `buildActiveOrderDetailsPageUrl`, and `trackingNumber` by `buildTrackDeliveryUrl`, without
 * being exposed as a row property of their own.
 */
export function mapActiveDistributionToRow(distribution: RawActiveDistribution): PurchaseHistoryRow {
  const distributionType = toFulfillmentType(distribution.distributionType);

  return {
    orderNumber: distribution.orderNumber ?? '',
    projectName: orUndefined(distribution.projectName),
    poJobName: orUndefined(distribution.poJobName),
    distributionType,
    status: distribution.status ?? '',
    distributionDate: distribution.distributionDate ?? '',
    distributionTime: orUndefined(distribution.distributionTime),
    lineItems: toLineItems(distribution.lineItems),
    location: {
      addressLine1: orUndefined(distribution.location?.addressLine1),
      storeId: orUndefined(distribution.location?.storeId)
    },
    totalAmount: sumLineItemTotals(distribution.lineItems),
    pickupPersonName: distributionType === 'PICKUP' ? orUndefined(distribution.recipient?.name) : undefined,
    contactNumber: distributionType === 'DELIVERY' ? orUndefined(distribution.recipient?.phoneNumber) : undefined,
    viewDetailsUrl: buildActiveOrderDetailsPageUrl(distribution),
    trackDeliveryUrl: buildTrackDeliveryUrl(distribution)
  };
}
